import React, { useState, useEffect } from 'react';
import { 
  X, 
  ChevronRight, 
  Home, 
  Folder, 
  CheckSquare, 
  Plus, 
  Edit, 
  Trash2, 
  MoreVertical,
  ArrowLeft,
  ArrowRight,
  Save,
  Calendar,
  User,
  Tag,
  MessageSquare,
  Link,
  Copy,
  Share2,
  Archive,
  Star,
  Eye,
  ExternalLink,
  Settings,
  FileText,
  Clock,
  AlertCircle,
  CheckCircle,
  Play,
  Pause,
  Zap
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

export interface BreadcrumbItem {
  id: string;
  title: string;
  type: 'home' | 'project' | 'task' | 'subproject' | 'subtask';
  data?: any;
}

export interface SideModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  breadcrumbs: BreadcrumbItem[];
  onBreadcrumbClick?: (item: BreadcrumbItem) => void;
  actions?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showBackButton?: boolean;
  onBack?: () => void;
  showForwardButton?: boolean;
  onForward?: () => void;
}

const SideModal: React.FC<SideModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  breadcrumbs,
  onBreadcrumbClick,
  actions,
  size = 'lg',
  showBackButton = false,
  onBack,
  showForwardButton = false,
  onForward
}) => {
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();

  const sizeClasses = {
    sm: 'w-96',
    md: 'w-[500px]',
    lg: 'w-[700px]',
    xl: 'w-[900px]'
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
      />
      
      {/* Side Modal */}
      <div className={`fixed top-0 right-0 h-full ${sizeClasses[size]} bg-white dark:bg-slate-800 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 sticky top-0 z-10">
          <div className="flex items-center space-x-3">
            {/* Navigation Buttons */}
            <div className="flex items-center space-x-2">
              {showBackButton && (
                <button
                  onClick={onBack}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  title="Go back"
                >
                  <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                </button>
              )}
              
              {showForwardButton && (
                <button
                  onClick={onForward}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  title="Go forward"
                >
                  <ArrowRight className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                </button>
              )}
            </div>

            {/* Breadcrumbs */}
            <div className="flex items-center space-x-2 text-sm">
              {breadcrumbs.map((item, index) => (
                <React.Fragment key={item.id}>
                  {index > 0 && (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  )}
                  <button
                    onClick={() => onBreadcrumbClick?.(item)}
                    className={`flex items-center space-x-1 px-2 py-1 rounded-md transition-colors ${
                      index === breadcrumbs.length - 1
                        ? 'text-slate-900 dark:text-white font-medium'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    {item.type === 'home' && <Home className="w-3 h-3" />}
                    {item.type === 'project' && <Folder className="w-3 h-3" />}
                    {item.type === 'task' && <CheckSquare className="w-3 h-3" />}
                    {item.type === 'subproject' && <Folder className="w-3 h-3" />}
                    {item.type === 'subtask' && <CheckSquare className="w-3 h-3" />}
                    <span className="truncate max-w-32">{item.title}</span>
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Actions and Close */}
          <div className="flex items-center space-x-2">
            {actions}
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="h-full overflow-y-auto">
          {children}
        </div>
      </div>
    </>
  );
};

export default SideModal;
